import { useContext, useMemo } from "react";
import { SettingKey } from "@swagger/typescript-fetch-goflyer";
import { GoFlyerAdminContext } from "../contextApi/context";

interface ColumnConfig {
  key: string;
  order: number;
  visible: boolean;
}

interface TableSettingsConfig {
  columnsConfig: ColumnConfig[] | null;
  pageLimit: number;
}

/**
 * Returns the table column config and page limit from the current user's
 * settings for the given SettingKey. Falls back to sensible defaults when
 * no setting is found.
 */
export const useTableSettingsConfig = (
  settingKey: SettingKey,
  defaultPageSize = 100,
): TableSettingsConfig => {
  const { state } = useContext(GoFlyerAdminContext);

  return useMemo(() => {
    const userSettings = state.gfAdminUser?.settings || [];
    const tableConfigSetting = userSettings.find(
      (s) => s?.setting?.settingKey === settingKey,
    );

    const columnsConfig: ColumnConfig[] | null =
      tableConfigSetting?.value?.columns ?? null;

    const pageLimit: number =
      tableConfigSetting?.value?.pageLimit ?? defaultPageSize;

    return { columnsConfig, pageLimit };
  }, [state.gfAdminUser?.settings, settingKey, defaultPageSize]);
};

/**
 * Applies a columns config (from user settings) to a base column list.
 * Filters invisible columns and sorts by the stored order.
 * Columns not present in the config are kept at the end (order 999).
 */
export const applyColumnsConfig = <T extends { field: string }>(
  baseColumns: T[],
  columnsConfig: ColumnConfig[] | null,
): T[] => {
  if (!columnsConfig) return baseColumns;

  const configMap = new Map<string, ColumnConfig>();
  columnsConfig.forEach((c) => configMap.set(c.key, c));

  const result: (T & { order: number })[] = [];

  baseColumns.forEach((col) => {
    const config = configMap.get(col.field);
    if (config) {
      if (config.visible) {
        result.push({ ...col, order: config.order });
      }
    } else {
      result.push({ ...col, order: 999 });
    }
  });

  result.sort((a, b) => a.order - b.order);
  return result;
};
